import {
  type CreateMcpAllowlistEntry,
  type McpAllowlistEntry,
  McpAllowlistEntrySchema,
  type UpdateMcpAllowlistEntry,
} from '@sbc/domain-models/agents';
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiDelete, apiGet, apiSend } from './client';

const MCP_ALLOWLIST_KEY = ['admin', 'mcp-allowlist'];
const MCP_CONNECTIONS_KEY = ['admin', 'mcp-connections'];

// A write to the allowlist can change which tools a connection exposes, so both
// the allowlist and the connection list need refetching.
function invalidateAllowlist(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: MCP_ALLOWLIST_KEY });
  void queryClient.invalidateQueries({ queryKey: MCP_CONNECTIONS_KEY });
}

export function useMcpAllowlist(connectionId: string) {
  return useQuery<McpAllowlistEntry[]>({
    queryKey: [...MCP_ALLOWLIST_KEY, 'list', connectionId],
    queryFn: () =>
      apiGet(
        `/api/admin/mcp-allowlist?connectionId=${encodeURIComponent(connectionId)}`,
        McpAllowlistEntrySchema.array(),
      ),
  });
}

export function useCreateMcpAllowlistEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateMcpAllowlistEntry) =>
      apiSend('POST', '/api/admin/mcp-allowlist', input, McpAllowlistEntrySchema),
    onSuccess: () => invalidateAllowlist(queryClient),
  });
}

export function useUpdateMcpAllowlistEntry(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateMcpAllowlistEntry) =>
      apiSend('PUT', `/api/admin/mcp-allowlist/${id}`, input, McpAllowlistEntrySchema),
    onSuccess: () => invalidateAllowlist(queryClient),
  });
}

export function useDeleteMcpAllowlistEntry(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiDelete(`/api/admin/mcp-allowlist/${id}`),
    onSuccess: () => invalidateAllowlist(queryClient),
  });
}

export function useDeleteAllMcpAllowlistEntries(connectionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiDelete(`/api/admin/mcp-allowlist?connectionId=${encodeURIComponent(connectionId)}`),
    onSuccess: () => invalidateAllowlist(queryClient),
  });
}
